import React, { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { VCard } from "../../../types/vcard";
import PremiumFeatureNotice from "../shared/PremiumFeatureNotice";

interface TechDevice {
  id: string;
  deviceName: string;
  serialNumber: string;
  modelNumber: string;
  deviceType: string;
}

interface DeviceListProps {
  formData: Partial<VCard>;
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  maxDevices: number;
  onMaxDevicesReached?: () => void;
}

const createDeviceId = () => {
  const webCrypto = globalThis.crypto;

  if (
    typeof webCrypto !== "undefined" &&
    typeof webCrypto.randomUUID === "function"
  ) {
    return webCrypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const DeviceList: React.FC<DeviceListProps> = ({
  formData,
  handleChange,
  maxDevices,
  onMaxDevicesReached,
}) => {
  const [showPremiumNotice, setShowPremiumNotice] = useState(false);
  const [devices, setDevices] = useState<TechDevice[]>(
    formData.categorySpecificInfo?.myTech?.devices || [
      {
        id: "1",
        deviceName: "",
        serialNumber: "",
        modelNumber: "",
        deviceType: "",
      },
    ],
  );

  // Use effect to update devices when formData changes
  useEffect(() => {
    const existingDevices = formData.categorySpecificInfo?.myTech?.devices;
    if (existingDevices && existingDevices.length > 0) {
      setDevices((currentDevices) =>
        JSON.stringify(currentDevices) === JSON.stringify(existingDevices)
          ? currentDevices
          : existingDevices,
      );
    }
  }, [formData.categorySpecificInfo?.myTech?.devices]);

  const handleDeviceChange = (
    id: string,
    field: keyof TechDevice,
    value: string,
  ) => {
    const updatedDevices = devices.map((device) => {
      if (device.id === id) {
        return { ...device, [field]: value };
      }
      return device;
    });
    setDevices(updatedDevices);
    updateFormData(updatedDevices);
  };

  const updateFormData = (updatedDevices: TechDevice[]) => {
    handleChange({
      target: {
        name: "categorySpecificInfo.myTech.devices",
        value: updatedDevices,
      },
    } as any);
  };

  const addDevice = () => {
    if (devices.length >= maxDevices) {
      setShowPremiumNotice(true);
      onMaxDevicesReached?.();
      return;
    }
    const updatedDevices = [
      ...devices,
      {
        id: createDeviceId(),
        deviceName: "",
        serialNumber: "",
        modelNumber: "",
        deviceType: "",
      },
    ];

    setDevices(updatedDevices);
    updateFormData(updatedDevices);
  };

  const removeDevice = (id: string) => {
    const newDevices = devices.filter((device) => device.id !== id);
    setDevices(newDevices);
    updateFormData(newDevices);
  };

  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
        Device List
      </h3>
      <div className="space-y-6">
        {devices.map((device) => (
          <div
            key={device.id}
            className="space-y-4 rounded-lg bg-gray-50 p-4 dark:bg-slate-900/40"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Device Name*
                </label>
                <input
                  type="text"
                  value={device.deviceName}
                  onChange={(e) =>
                    handleDeviceChange(device.id, "deviceName", e.target.value)
                  }
                  placeholder="e.g., iPhone 13 Pro"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>
              {devices.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDevice(device.id)}
                  className="ml-2 p-2 text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  value={device.serialNumber}
                  onChange={(e) =>
                    handleDeviceChange(
                      device.id,
                      "serialNumber",
                      e.target.value,
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Model Number
                </label>
                <input
                  type="text"
                  value={device.modelNumber}
                  onChange={(e) =>
                    handleDeviceChange(device.id, "modelNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Device Type
              </label>
              <select
                value={device.deviceType}
                onChange={(e) =>
                  handleDeviceChange(device.id, "deviceType", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select device type</option>
                <option value="phone">Phone</option>
                <option value="tablet">Tablet</option>
                <option value="laptop">Laptop</option>
                <option value="camera">Camera</option>
                <option value="drone">Drone</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        ))}

        {showPremiumNotice ? (
          <PremiumFeatureNotice
            message={`Adding more than ${maxDevices} devices requires a premium subscription. Upgrade your account to add unlimited devices.`}
            onDismiss={() => setShowPremiumNotice(false)}
          />
        ) : (
          <button
            type="button"
            onClick={addDevice}
            className="inline-flex items-center space-x-2 text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
          >
            <Plus className="h-4 w-4" />
            <span>Add Another Device</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DeviceList;
